/**
 * Zahlungsstand eines Vorgangs als Abzeichen, Datumszeile und Verlaufstext (Antoine, 19.10.2026,
 * Mockup „Zahlungsstand“ Abschnitte A+B freigegeben). Rein lesend und rein anzeigend: alle Zahlen
 * kommen fertig vom Server (`abrechnungDeckung`, `checkoutDeckung`, Zahlungen, das Protokoll
 * `payment.succeeded`) — hier wird nichts gerechnet außer dem Vergleich „ist schon Geld da / ist
 * etwas fällig“. Beträge stammen nie aus dem Client.
 *
 * Eine Funktion für alle Orte (Kopf des Vorgangs, Buchungsliste, Übersicht, Kundenprofil):
 * `zahlungsstandMeta`. `restOffenMeta` (nach der Abrechnung) ist ein Sonderfall davon und ruft sie.
 */
import { PAYMENT_TYPE_LABEL, money, type StatusMeta } from "../ui/format";
import { kennzahlenFuerVorgang, nettoBezahltCents, type AbrechnungsDeckung, type ZahlungLike } from "./zahlungs-kennzahlen";

/**
 * Grün für „bezahlt“ (Antoine, 19.10.2026, Mockup Zahlungsstand freigegeben): Erfolg ist grün, auch wo
 * `tertiary` seit 13.10. (Ruhige Hinweise) grau ist. Die EINZIGE Stelle mit diesen Hex-Werten —
 * `test/ruhige-hinweise.test.ts` nimmt genau diese Datei als Ausnahme aus.
 */
export const GRUEN_TEXT = "text-[#217A50] dark:text-[#6FCF97]";
export const GRUEN_RAHMEN = "border-[#217A50]/45 dark:border-[#6FCF97]/50";
export const GRUEN_UMRISS = "outline outline-1 -outline-offset-1 outline-[#217A50]/45 dark:outline-[#6FCF97]/50";
export const GRUEN_FUELLUNG = "bg-[#217A50] dark:bg-[#6FCF97]";
export const GRUEN_PILL = "bg-[#217A50]/10 text-[#217A50] dark:bg-[#6FCF97]/[0.12] dark:text-[#6FCF97]";

export interface ZahlungsPosten extends ZahlungLike {
  paymentType?: string;
  paidAt?: string | null;
}

export interface ListenZahlungsstand {
  bezahltCents: number;
  gesamtCents: number;
  gedeckt: boolean;
  faelligOffenCents: number;
  faelligSeit: string | null;
}

export interface ZahlungsstandEingabe {
  amountTotalCents?: number | null;
  abrechnungDeckung?: AbrechnungsDeckung | null;
  /** GET /api/events (Liste): Zahlungsstand je Vorgang aus `payments` (Server, eine gruppierte Abfrage). */
  zahlungsstand?: ListenZahlungsstand | null;
  /** Nur in der Liste (GET /api/events) und nur für Checkout-Vorgänge gefüllt. */
  checkoutDeckung?: { gesamtCents: number; bezahltCents: number; offenCents: number; gedeckt: boolean } | null;
}

export interface Zahlungsstand {
  gesamtCents: number | null;
  bezahltCents: number;
  offenCents: number;
  /** Alles da: Deckung erreicht und mindestens etwas bezahlt. */
  gedeckt: boolean;
  /** Mindestens eine Forderung ist fällig oder überfällig (oder die Abrechnung ist abgeschlossen). */
  faellig: boolean;
  ausAbrechnung: boolean;
}

function heuteIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Der Zahlungsstand aus dem, was die Seite hat. Mit Zahlungen (Vorgangsseite) genau, ohne Zahlungen
 * (Listen) nur aus den Deckungen des Servers; sonst `null` — ohne Grundlage wird nichts behauptet.
 */
export function zahlungsstand(e: ZahlungsstandEingabe, payments?: ZahlungsPosten[] | null, heute: string = heuteIso()): Zahlungsstand | null {
  const abr = e.abrechnungDeckung ?? null;
  if (abr) {
    return {
      gesamtCents: abr.gesamtCents,
      bezahltCents: abr.bezahltCents,
      offenCents: abr.offenCents,
      gedeckt: abr.gedeckt && abr.bezahltCents > 0,
      // Nach dem Abschluss der Abrechnung gilt ein Rest immer als einzufordern (#1515 H2).
      faellig: !abr.gedeckt && abr.offenCents > 0,
      ausAbrechnung: true,
    };
  }
  if (payments) {
    const kz = kennzahlenFuerVorgang(payments, { amountTotalCents: e.amountTotalCents ?? null });
    const faellig = payments.some((p) => (p.status === "pending" || p.status === "processing") && p.dueDate != null && p.dueDate.slice(0, 10) <= heute);
    return {
      gesamtCents: kz.sollCents,
      bezahltCents: kz.paidCents,
      offenCents: kz.openCents,
      gedeckt: kz.vollstaendigBezahlt,
      faellig: faellig && !kz.vollstaendigBezahlt && kz.openCents > 0,
      ausAbrechnung: false,
    };
  }
  const l = e.zahlungsstand ?? null;
  if (l) {
    const offen = l.gesamtCents > 0 ? Math.max(0, l.gesamtCents - l.bezahltCents) : l.faelligOffenCents;
    return {
      gesamtCents: l.gesamtCents > 0 ? l.gesamtCents : null,
      bezahltCents: l.bezahltCents,
      offenCents: offen,
      gedeckt: l.gedeckt && l.bezahltCents > 0,
      faellig: l.faelligOffenCents > 0 && !l.gedeckt,
      ausAbrechnung: false,
    };
  }
  const c = e.checkoutDeckung ?? null;
  if (c) {
    return { gesamtCents: c.gesamtCents, bezahltCents: c.bezahltCents, offenCents: c.offenCents, gedeckt: c.gedeckt && c.bezahltCents > 0, faellig: false, ausAbrechnung: false };
  }
  return null;
}

/**
 * EIN Nebenstatus neben dem Hauptstatus:
 *  - Geld da, aber noch nicht alles, nichts fällig  → „Anzahlung bezahlt“ (Betrag im Tooltip)
 *  - etwas fällig/überfällig oder Rest nach Abrechnung → „Rest offen 272,00 €“
 *  - alles bezahlt oder nichts zu sagen              → `null` (der Hauptstatus „Bezahlt“ trägt es)
 */
export function zahlungsstandMeta(e: ZahlungsstandEingabe, payments?: ZahlungsPosten[] | null, heute?: string): StatusMeta | null {
  const s = zahlungsstand(e, payments, heute);
  if (!s || s.gedeckt) return null;
  const titel = s.gesamtCents != null && s.gesamtCents > 0 ? `${money(s.bezahltCents)} von ${money(s.gesamtCents)}` : undefined;
  if (s.faellig && s.offenCents > 0) {
    return { label: `Rest offen ${money(s.offenCents)}`, tone: "warn", icon: "schedule", ...(titel ? { title: `${titel} bezahlt` } : {}) };
  }
  if (s.bezahltCents > 0 && s.offenCents > 0) {
    return { label: "Anzahlung bezahlt", tone: "success", icon: "check_circle", gruen: true, ...(titel ? { title: titel } : {}) };
  }
  return null;
}

/**
 * Vollständig bezahlt für den grünen Rahmen in Listen und Karten: Status „Bezahlt“ oder eine vom
 * Server gemeldete Deckung. Storniert/abgesagt bekommt nie einen Rahmen.
 */
export function istVollstaendigBezahlt(e: ZahlungsstandEingabe & { status?: string }, payments?: ZahlungsPosten[] | null): boolean {
  if (e.status === "cancelled" || e.status === "offer_declined" || e.status === "refunded") return false;
  if (e.status === "paid") return true;
  return zahlungsstand(e, payments)?.gedeckt ?? false;
}

/** Wert für die Schrittleiste: Schritt „Bezahlt“ halb gefüllt bei Anzahlung, grün bei Deckung. */
export function zahlungsSchrittArt(s: Zahlungsstand | null): "halb" | "gruen" | null {
  if (!s) return null;
  if (s.gedeckt) return "gruen";
  return s.bezahltCents > 0 ? "halb" : null;
}

/** „28.09.“ in deutscher Zeit — ohne Jahr, wie im Mockup. */
function kurzDatum(iso: string): string | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", timeZone: "Europe/Berlin" });
}

/** „Anzahlung 28.09. · Restzahlung 12.10.“ — je bezahlter Zahlung mit Datum; sonst `null`. */
export function bezahltDatenZeile(payments: ZahlungsPosten[]): string | null {
  const teile = payments
    .filter((p) => p.status === "succeeded" && nettoBezahltCents(p) > 0 && p.paidAt)
    .sort((a, b) => (a.paidAt as string).localeCompare(b.paidAt as string))
    .flatMap((p) => {
      const datum = kurzDatum(p.paidAt as string);
      return datum ? [`${PAYMENT_TYPE_LABEL[p.paymentType ?? ""] ?? "Zahlung"} ${datum}`] : [];
    });
  return teile.length > 0 ? teile.join(" · ") : null;
}

/** „Anzahlung bezahlt“ / „bezahlt“ — Beschriftung hinter dem bezahlten Betrag in der Zahlungen-Karte. */
export function bezahltBeschriftung(payments: ZahlungsPosten[], vollstaendig: boolean): string {
  const bezahlte = payments.filter((p) => p.status === "succeeded" && nettoBezahltCents(p) > 0);
  if (vollstaendig || bezahlte.length !== 1) return "bezahlt";
  const typ = bezahlte[0]!.paymentType;
  return typ && PAYMENT_TYPE_LABEL[typ] ? `${PAYMENT_TYPE_LABEL[typ]} bezahlt` : "bezahlt";
}

export interface ZahlungVerlaufText {
  titel: string;
  /** Nachsatz, z. B. „noch offen 272,00 €“. */
  detail?: string;
  icon: string;
  /** Zusätzlicher Abschluss-Eintrag, rein aus demselben Protokolleintrag abgeleitet. */
  abschluss?: { titel: string; icon: string };
}

function zahl(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

/**
 * `payment.succeeded` aus den Protokoll-Details (`{amountCents, totalCents, paidCents, openCents,
 * covered}`, apps/api/src/lib/payment-transitions.ts) in einen sprechenden Text. Ältere Einträge
 * ohne Betrag → `null` (es bleibt „Zahlung eingegangen“). Die Art der Zahlung (`paymentType`) kennt
 * das Protokoll nicht, nur die Zahlung selbst; fehlt sie, gilt: war vorher noch nichts bezahlt
 * (bezahlt gesamt = dieser Betrag) und ist es nicht gedeckt, ist es die Anzahlung.
 */
export function zahlungEingegangenText(details: Record<string, unknown> | null | undefined, paymentType?: string | null): ZahlungVerlaufText | null {
  const betrag = zahl(details?.amountCents);
  if (betrag == null) return null;
  const gesamt = zahl(details?.totalCents);
  const bezahlt = zahl(details?.paidCents);
  const offen = zahl(details?.openCents);
  const gedeckt = details?.covered === true;
  const anzahlungsArt = paymentType === "deposit" || paymentType === "prepayment";
  const frueherBezahlt = bezahlt != null && bezahlt > betrag;

  if (gedeckt) {
    const rest = !anzahlungsArt && (paymentType === "final" || frueherBezahlt);
    return {
      titel: `${rest ? "Restzahlung bezahlt" : "Zahlung eingegangen"} · ${money(betrag)}`,
      icon: "check_circle",
      ...(gesamt != null && bezahlt != null ? { abschluss: { titel: `Vollständig bezahlt · ${money(bezahlt)} von ${money(gesamt)}`, icon: "verified" } } : {}),
    };
  }
  if (details?.covered === false) {
    const anzahlung = anzahlungsArt || (paymentType == null && !frueherBezahlt);
    return {
      titel: `${anzahlung ? "Anzahlung bezahlt" : "Zahlung eingegangen"} · ${money(betrag)}`,
      icon: anzahlung ? "check" : "check_circle",
      ...(offen != null && offen > 0 ? { detail: `noch offen ${money(offen)}` } : {}),
    };
  }
  return { titel: `Zahlung eingegangen · ${money(betrag)}`, icon: "check_circle" };
}
